/**
 * API Configuration
 * 
 * Centralized configuration for all API base URLs, sourced from environment variables.
 * Normalizes API URLs to ensure proper formatting and includes environment-specific
 * defaults. Handles development proxy paths and production URLs consistently.
 */

import logger from '@/shared/utils/logger.js';

const rawApiUrl = import.meta.env.VITE_API_URL;

/**
 * Normalizes the authentication base URL to ensure proper formatting.
 * @param {string} raw - The raw API URL from environment variables
 * @returns {string} Normalized authentication base URL
 */
function normalizeAuthBaseUrl(raw) {
  if (!raw || !raw.trim()) {
    logger.error('VITE_API_URL environment variable is missing — falling back to gateway.');
    return 'https://gateway.ismartghana.com/auth-service/api';
  }

  let cleaned = raw.trim().replace(/\/+$/, '');
  if (!cleaned.startsWith('http')) {
    return cleaned;
  }

  if (!/\/api$/.test(cleaned)) {
    cleaned = `${cleaned}/api`;
  }
  return cleaned;
}

/**
 * Normalizes disbursement base URL
 * @param {string} raw - The raw API URL from environment variables
 * @returns {string} Normalized disbursement base URL
 */
function normalizeDisbursementBaseUrl(raw) {
  if (!raw || !raw.trim()) {
    logger.error('VITE_DISBURSEMENT_API_URL environment variable is missing — falling back to gateway.');
    return 'https://gateway.ismartghana.com/disbursement-service/api';
  }

  let cleaned = raw.trim().replace(/\/+$/, '');
  if (!/\/api$/.test(cleaned)) {
    cleaned = `${cleaned}/api`;
  }
  return cleaned;
}

/**
 * Normalizes collection base URL
 * @param {string} raw - The raw API URL from environment variables
 * @returns {string} Normalized collection base URL
 */
function normalizeCollectionBaseUrl(raw) {
  if (!raw || !raw.trim()) {
    logger.error('VITE_COLLECTION_API_URL environment variable is missing.');
    return '';
  }

  return raw.trim().replace(/\/+$/, '');
}

/**
 * Normalizes utility base URL
 * @param {string} raw - The raw API URL from environment variables
 * @returns {string} Normalized utility base URL
 */
function normalizeUtilityBaseUrl(raw) {
  if (!raw || !raw.trim()) {
    logger.error('VITE_UTILITY_API_URL environment variable is missing.');
    return '';
  }

  return raw.trim().replace(/\/+$/, '');
}

export const API_CONFIG = {
  AUTH_BASE_URL: normalizeAuthBaseUrl(rawApiUrl),
  AUTH_REFRESH_ENDPOINT: import.meta.env.VITE_AUTH_REFRESH_ENDPOINT || '/auth/token/refresh',
  DISBURSEMENT_BASE_URL: normalizeDisbursementBaseUrl(import.meta.env.VITE_DISBURSEMENT_API_URL),
  COLLECTION_BASE_URL: normalizeCollectionBaseUrl(import.meta.env.VITE_COLLECTION_API_URL),
  UTILITY_BASE_URL: normalizeUtilityBaseUrl(import.meta.env.VITE_UTILITY_API_URL),
  SMS_BASE_URL: import.meta.env.VITE_SMS_BASE_URL || '',
};
